import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Save, Plus, X } from "lucide-react";
import { SidebarProvider } from "@/components/ui/sidebar";
import Header from "../Header";
import AdminSidebar from "../Sidebar";
import { db } from "../../firebase/config";
import { collection, doc, setDoc, getDocs, query, where } from "firebase/firestore";
import { getAuth, onAuthStateChanged, IdTokenResult } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useToast } from "@/components/ui/use-toast";
import { useNavigate } from "react-router-dom";

interface Affiliation {
  role: string;
  schoolID?: string;
}

interface ClassData {
  level: number;
  name: string;
  stream: string;
  schoolID: string;
  classUid: string;
  createdAt: Date;
  submittedBy: string;
  academicYear: string;
  enrolled: number;
  capacity: number;
  namingConvention: string;
  stage?: string;
  nextClassUid?: string; // NEW: Direct pointer to the next class in promotion chain
}

interface ClassRegistrationFormProps {
  onBack: () => void;
  onSave: (classData: any) => void;
  hierarchy?: any[];
}

const ClassRegistrationForm = ({ onBack, onSave }: ClassRegistrationFormProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const currentYear = new Date().getFullYear();

  // Auth state
  const [userId, setUserId] = useState<string | null>(null);
  const [schoolID, setSchoolID] = useState<string | null>(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [hasValidClaims, setHasValidClaims] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    elevationLevel: '4',
    className: 'Form',
    startingLevel: '1',
    streamsPerLevel: '2',
    streamNames: [''],
    stage: 'progressive' as 'progressive' | 'final',
  });

  const [previewLevels, setPreviewLevels] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isUpdatingHierarchy, setIsUpdatingHierarchy] = useState(false);

  // ============ UPDATE EXISTING CLASSES HIERARCHY ============
  const updateExistingClassesHierarchy = async () => {
    if (!schoolID || !hasValidClaims) {
      toast({
        title: "Error",
        description: "You must be logged in as an admin to update classes.",
        variant: "destructive",
      });
      return;
    }

    setIsUpdatingHierarchy(true);
    try {
      // Call cloud function instead of direct Firestore writes
      const functions = getFunctions();
      const rebuildHierarchyFn = httpsCallable(functions, 'rebuildHierarchy');
      
      const result = await rebuildHierarchyFn({}) as any;
      
      if (result.data.success) {
        toast({
          title: "Hierarchy Updated!",
          description: result.data.message || `Successfully updated promotion chain for ${result.data.updatedCount} classes.`,
          duration: 5000,
        });
      } else {
        throw new Error(result.data.message || "Unknown error");
      }

    } catch (error: any) {
      console.error("[UpdateHierarchy] Error:", error);
      toast({
        title: "Update Failed",
        description: error.message || "Failed to update class hierarchy.",
        variant: "destructive",
      });
    } finally {
      setIsUpdatingHierarchy(false);
    }
  };

  /* ── Auth & Claims Verification ──────────────────────────────────── */
  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setUserId(user.uid);
        try {
          if (process.env.NODE_ENV !== "production") {
            console.log("Authenticating user with uid:", user.uid);
          }
          await user.getIdToken(true);
          const idTokenResult: IdTokenResult = await user.getIdTokenResult(true);
          if (!idTokenResult) {
            throw new Error("Failed to retrieve user token claims");
          }
          const affiliations = (idTokenResult.claims.affiliations as Affiliation[] | undefined) || [];
          const defaultAffiliation = idTokenResult.claims.defaultAffiliation as Affiliation | undefined;
          const claimSchoolID = idTokenResult.claims.schoolID as string | undefined;

          if (process.env.NODE_ENV !== "production") {
            console.log("ID Token Result claims:", JSON.stringify(idTokenResult.claims, null, 2));
            console.log("Affiliations:", affiliations, "Default Affiliation:", defaultAffiliation, "Claim schoolID:", claimSchoolID);
          }

          let selectedAffiliation: Affiliation | null = null;
          if (defaultAffiliation && defaultAffiliation.role === "admin" && defaultAffiliation.schoolID) {
            selectedAffiliation = defaultAffiliation;
          } else if (affiliations.length > 0) {
            selectedAffiliation = affiliations.find(aff => aff.role === "admin" && aff.schoolID) || affiliations[0];
          }

          if (selectedAffiliation?.role === "admin" && selectedAffiliation.schoolID && claimSchoolID === selectedAffiliation.schoolID) {
            const selectedSchoolID = selectedAffiliation.schoolID;
            if (process.env.NODE_ENV !== "production") {
              console.log("Using schoolID from selectedAffiliation:", selectedSchoolID);
            }
            setSchoolID(selectedSchoolID);
            setHasValidClaims(true);
          } else {
            if (process.env.NODE_ENV !== "production") {
              console.log("Invalid or missing admin claims, retrying after 10 seconds...");
            }
            await new Promise(resolve => setTimeout(resolve, 10000));
            await user.getIdToken(true);
            const retryIdTokenResult = await user.getIdTokenResult(true);
            const retryAffiliations = (retryIdTokenResult.claims.affiliations as Affiliation[] | undefined) || [];
            const retryDefaultAff = retryIdTokenResult.claims.defaultAffiliation as Affiliation | undefined;
            const retrySchoolID = retryIdTokenResult.claims.schoolID as string | undefined;

            if (process.env.NODE_ENV !== "production") {
              console.log("Retry claims:", JSON.stringify(retryIdTokenResult.claims, null, 2));
            }

            let retrySelectedAffiliation: Affiliation | null = null;
            if (retryDefaultAff && retryDefaultAff.role === "admin" && retryDefaultAff.schoolID) {
              retrySelectedAffiliation = retryDefaultAff;
            } else if (retryAffiliations.length > 0) {
              retrySelectedAffiliation = retryAffiliations.find(aff => aff.role === "admin" && aff.schoolID) || retryAffiliations[0];
            }

            if (retrySelectedAffiliation?.role === "admin" && retrySelectedAffiliation.schoolID && retrySchoolID === retrySelectedAffiliation.schoolID) {
              setSchoolID(retrySelectedAffiliation.schoolID);
              setHasValidClaims(true);
            } else {
              throw new Error(
                `Invalid token claims: affiliations=${JSON.stringify(retryAffiliations)}, ` +
                `defaultAffiliation=${JSON.stringify(retryDefaultAff)}, schoolID=${retrySchoolID}`
              );
            }
          }
        } catch (error: any) {
          console.error("Error verifying user claims:", error);
          toast({
            title: "Missing Permissions",
            description: "You lack the necessary permissions to register classes. Please contact support.",
            variant: "destructive",
            duration: 5000,
          });
          setHasValidClaims(false);
          navigate("/login-page");
        }
      } else {
        setUserId(null);
        setSchoolID(null);
        setHasValidClaims(false);
        toast({
          title: "Authentication Error",
          description: "No user logged in. Please log in and try again.",
          variant: "destructive",
          duration: 5000,
        });
        navigate("/login-page");
      }
      setIsAuthLoading(false);
    });

    return () => {
      if (process.env.NODE_ENV !== "production") {
        console.log("Unsubscribing from onAuthStateChanged");
      }
      unsubscribe();
    };
  }, [navigate, toast]);

  /* ── Generate Preview Levels ────────────────────────────────────── */
  useEffect(() => {
    const levels = parseInt(formData.elevationLevel);
    const startLevel = parseInt(formData.startingLevel);
    if (levels > 0 && formData.className && startLevel >= 1) {
      const generatedLevels = Array.from({ length: levels }, (_, i) => 
        `${formData.className} ${startLevel + i}`
      );
      setPreviewLevels(generatedLevels);
    } else {
      setPreviewLevels([]);
    }
  }, [formData.elevationLevel, formData.className, formData.startingLevel]);

  /* ── Update Stream Names Array ──────────────────────────────────── */
  useEffect(() => {
    const streamsCount = parseInt(formData.streamsPerLevel);
    if (streamsCount > 0) {
      const currentNames = formData.streamNames;
      if (currentNames.length < streamsCount) {
        const newNames = [...currentNames, ...Array(streamsCount - currentNames.length).fill('')];
        setFormData(prev => ({ ...prev, streamNames: newNames }));
      } else if (currentNames.length > streamsCount) {
        setFormData(prev => ({ ...prev, streamNames: currentNames.slice(0, streamsCount) }));
      }
    }
  }, [formData.streamsPerLevel]);

  /* ── Input Handlers ────────────────────────────────────────────── */
  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleStreamNameChange = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      streamNames: prev.streamNames.map((name, i) => i === index ? value : name)
    }));
  };

  const removeStreamName = (index: number) => {
    if (formData.streamNames.length > 1) {
      setFormData(prev => ({
        ...prev,
        streamNames: prev.streamNames.filter((_, i) => i !== index),
        streamsPerLevel: (parseInt(prev.streamsPerLevel) - 1).toString()
      }));
    }
  };

  const addStreamName = () => {
    setFormData(prev => ({
      ...prev,
      streamNames: [...prev.streamNames, ''],
      streamsPerLevel: (parseInt(prev.streamsPerLevel) + 1).toString()
    }));
  };

  /* ── Submit Handler ────────────────────────────────────────────── */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!schoolID || !userId || !hasValidClaims) {
      toast({
        title: "Authentication Error",
        description: "User, school ID, or permissions missing. Please log in again.",
        variant: "destructive",
        duration: 5000,
      });
      navigate("/login-page");
      return;
    }

    const user = getAuth().currentUser;
    if (!user) {
      toast({
        title: "Authentication Error",
        description: "User is not authenticated. Please log in again.",
        variant: "destructive",
        duration: 5000,
      });
      navigate("/login-page");
      return;
    }

    const { elevationLevel, className, startingLevel, streamNames, streamsPerLevel, stage } = formData;
    const levels = parseInt(elevationLevel);
    const startLevel = parseInt(startingLevel);
    const streamsCount = parseInt(streamsPerLevel);

    // Validate inputs
    if (levels <= 0 || startLevel <= 0 || streamsCount <= 0 || !className.trim()) {
      toast({
        title: "Invalid Input",
        description: "Elevation level, starting level, streams per level, and class name must be valid and non-empty.",
        variant: "destructive",
        duration: 5000,
      });
      return;
    }

    const validStreamNames = streamNames.filter(name => name && name.trim() !== '');
    if (validStreamNames.length !== streamsCount) {
      toast({
        title: "Invalid Stream Names",
        description: "Please fill in all stream names.",
        variant: "destructive",
        duration: 5000,
      });
      return;
    }

    setIsSaving(true);
    try {
      await user.getIdToken(true);
      const idTokenResult = await user.getIdTokenResult(true);
      const affiliations = (idTokenResult.claims.affiliations as Affiliation[] | undefined) || [];
      const defaultAffiliation = idTokenResult.claims.defaultAffiliation as Affiliation | undefined;
      const claimSchoolID = idTokenResult.claims.schoolID as string | undefined;

      if (process.env.NODE_ENV !== "production") {
        console.log("Claims before write:", JSON.stringify({ affiliations, defaultAffiliation, claimSchoolID }, null, 2));
      }

      const adminAffiliation = affiliations.find(aff => aff.role === "admin" && aff.schoolID === schoolID);
      if (
        !adminAffiliation &&
        (!defaultAffiliation || defaultAffiliation.role !== "admin" || defaultAffiliation.schoolID !== schoolID) &&
        claimSchoolID !== schoolID
      ) {
        if (process.env.NODE_ENV !== "production") {
          console.log("Invalid admin claims, retrying after 10 seconds...");
        }
        await new Promise(resolve => setTimeout(resolve, 10000));
        await user.getIdToken(true);
        const retryIdTokenResult = await user.getIdTokenResult(true);
        const retryAffiliations = (retryIdTokenResult.claims.affiliations as Affiliation[] | undefined) || [];
        const retryDefaultAff = retryIdTokenResult.claims.defaultAffiliation as Affiliation | undefined;
        const retrySchoolID = retryIdTokenResult.claims.schoolID as string | undefined;

        if (process.env.NODE_ENV !== "production") {
          console.log("Retry claims:", JSON.stringify({ retryAffiliations, retryDefaultAff, retrySchoolID }, null, 2));
        }

        const retryAdminAffiliation = retryAffiliations.find(aff => aff.role === "admin" && aff.schoolID === schoolID);
        if (
          !retryAdminAffiliation &&
          (!retryDefaultAff || retryDefaultAff.role !== "admin" || retryDefaultAff.schoolID !== schoolID) &&
          retrySchoolID !== schoolID
        ) {
          throw new Error(
            `Invalid token claims: affiliations=${JSON.stringify(retryAffiliations)}, ` +
            `defaultAffiliation=${JSON.stringify(retryDefaultAff)}, schoolID=${retrySchoolID}`
          );
        }
      }

      const classesCollectionRef = collection(db, `schools/${schoolID}/classes`);
      const classesToSave: ClassData[] = [];

      // Generate classes for each level and stream
      for (let i = 0; i < levels; i++) {
        const level = startLevel + i;
        const levelName = `${className.trim()} ${level}`;
        for (const stream of validStreamNames) {
          const fullClassName = `${levelName} ${stream.trim()}`;

          // Check for duplicate class name
          const q = query(classesCollectionRef, where("name", "==", fullClassName));
          const querySnapshot = await getDocs(q);
          if (!querySnapshot.empty) {
            toast({
              title: "Class Name Already Exists",
              description: `A class named "${fullClassName}" already exists in your school.`,
              variant: "destructive",
              duration: 5000,
            });
            setIsSaving(false);
            return;
          }

          const classDocRef = doc(classesCollectionRef);
          const classData: ClassData = {
            level,
            name: fullClassName,
            stream: stream.trim(),
            schoolID,
            classUid: classDocRef.id,
            createdAt: new Date(),
            submittedBy: userId,
            academicYear: currentYear.toString(),
            enrolled: 0,
            capacity: 0,
            namingConvention: className.trim(),
          };

          // Set stage only for the last level, defaulting to "final"
          if (i === levels - 1) {
            classData.stage = stage || "final";
          }

          classesToSave.push(classData);
        }
      }

      // ============ SET UP HIERARCHY CHAIN ============
      // For each class, find and link to the next class in the chain
      // This creates an explicit promotion path: Form 1 blue -> Form 2 blue -> Form 3 blue -> etc.
      for (const currentClass of classesToSave) {
        // Skip final stage classes (they promote to Alumni, handled separately)
        if (currentClass.stage === 'final') {
          currentClass.nextClassUid = 'ALUMNI';
          continue;
        }
        
        // Find the next class: same stream, same namingConvention, level + 1
        const nextClass = classesToSave.find(c => 
          c.stream.toLowerCase() === currentClass.stream.toLowerCase() &&
          c.namingConvention.toLowerCase() === currentClass.namingConvention.toLowerCase() &&
          c.level === currentClass.level + 1
        );
        
        if (nextClass) {
          currentClass.nextClassUid = nextClass.classUid;
        }
        // If no next class found, nextClassUid remains undefined (handled by promotion logic)
      }

      // Save all classes with their hierarchy links
      for (const classData of classesToSave) {
        await setDoc(doc(classesCollectionRef, classData.classUid), {
          ...classData,
          createdAt: new Date(),
        });
      }

      toast({
        title: "Classes Registered Successfully!",
        description: `${classesToSave.length} classes have been registered successfully.`,
        duration: 3000,
      });

      onSave({
        elevationLevel: levels,
        className: className.trim(),
        startingLevel: startLevel,
        streamsPerLevel: streamsCount,
        streamNames: validStreamNames,
        stage: stage || "final",
        generatedClasses: classesToSave,
      });

      // Reset form
      setFormData({
        elevationLevel: '4',
        className: 'Form',
        startingLevel: '1',
        streamsPerLevel: '2',
        streamNames: [''],
        stage: 'progressive',
      });
      setPreviewLevels([]);

      setTimeout(() => {
        navigate("/classes");
      }, 1500);
    } catch (error: any) {
      console.error("Error registering classes:", error);
      if (error.code === "permission-denied") {
        toast({
          title: "Permission Error",
          description: "Unable to register classes due to insufficient permissions. Please ensure your account is set up as an admin and try again.",
          variant: "destructive",
          duration: 5000,
        });
      } else {
        toast({
          title: "Registration Failed",
          description: `Failed to register classes: ${error.message || "An unexpected error occurred."}`,
          variant: "destructive",
          duration: 5000,
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  /* ── Loading UI ────────────────────────────────────────────────── */
  if (isAuthLoading) {
    return (
      <SidebarProvider>
        <style>
          {`
            @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap');
            .font-roboto { font-family: 'Roboto', sans-serif; }
          `}
        </style>
        <div className="flex h-screen w-full bg-gray-50 dark:bg-gray-900 font-roboto">
          <AdminSidebar onSectionChange={() => {}} />
          <div className="flex flex-col flex-1 h-full">
            <Header />
            <main className="flex-1 p-3 sm:p-6 overflow-auto bg-gray-50 dark:bg-gray-900 flex justify-center items-center">
              <p className="text-gray-900 dark:text-white">Loading class registration form...</p>
            </main>
          </div>
        </div>
      </SidebarProvider>
    );
  }

  /* ── Unauthorized UI ───────────────────────────────────────────── */
  if (!hasValidClaims) {
    return (
      <SidebarProvider>
        <style>
          {`
            @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap');
            .font-roboto { font-family: 'Roboto', sans-serif; }
          `}
        </style>
        <div className="flex h-screen w-full bg-gray-50 dark:bg-gray-900 font-roboto">
          <AdminSidebar onSectionChange={() => {}} />
          <div className="flex flex-col flex-1 h-full">
            <Header />
            <main className="flex-1 p-3 sm:p-6 overflow-auto bg-gray-50 dark:bg-gray-900 flex justify-center items-center">
              <Card className="w-full max-w-md bg-[#FFFFFF] dark:bg-[#020817] border-2 border-[#E2E8F0] dark:border-[#1E293B]">
                <CardHeader>
                  <CardTitle className="text-lg sm:text-xl text-gray-900 dark:text-white">
                    Access Denied
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 dark:text-gray-400">
                    You do not have the necessary permissions to register classes. Please contact support or re-login.
                  </p>
                  <Button
                    onClick={() => navigate("/login-page")}
                    className="mt-4 bg-[#2563EB] hover:bg-[#1E40AF] dark:hover:bg-[#3B82F6] text-white border-2 border-[#E2E8F0] dark:border-[#1E293B]"
                  >
                    Go to Login
                  </Button>
                </CardContent>
              </Card>
            </main>
          </div>
        </div>
      </SidebarProvider>
    );
  }

  /* ── Main UI ──────────────────────────────────────────────────── */
  return (
    <SidebarProvider>
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap');
          .font-roboto { font-family: 'Roboto', sans-serif; }
        `}
      </style>
      <div className="flex h-screen w-full bg-gray-50 dark:bg-gray-900 font-roboto">
        <AdminSidebar onSectionChange={() => {}} />
        <div className="flex flex-col flex-1 h-full">
          <Header />
          <main className="flex-1 p-2 sm:p-4 lg:p-6 overflow-auto bg-gray-50 dark:bg-gray-900">
            <div className="max-w-6xl mx-auto">
              <div className="mb-4 sm:mb-6">
                <Button 
                  onClick={onBack} 
                  variant="outline" 
                  className="mb-4 flex items-center gap-2 bg-white dark:bg-[#020817] text-gray-900 dark:text-white border-2 border-[#E2E8F0] dark:border-[#1E293B] hover:bg-gray-50 dark:hover:bg-[#131E31]"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to Classes
                </Button>
                
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-2">
                  Academic Structure Setup
                </h1>
                <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mb-4">
                  Configure your school's academic levels and streams
                </p>
                
                {/* Update Existing Classes Button */}
                <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg mb-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                      <h3 className="font-medium text-amber-800 dark:text-amber-200">Update Existing Classes</h3>
                      <p className="text-sm text-amber-700 dark:text-amber-300">
                        Rebuild the promotion hierarchy chain for all existing classes. This fixes promotion paths.
                      </p>
                    </div>
                    <Button
                      type="button"
                      onClick={updateExistingClassesHierarchy}
                      disabled={isUpdatingHierarchy || !hasValidClaims}
                      className="bg-amber-600 hover:bg-amber-700 text-white shrink-0"
                    >
                      {isUpdatingHierarchy ? "Updating..." : "Update Hierarchy"}
                    </Button>
                  </div>
                </div>
              </div>

              <Card className="w-full bg-[#FFFFFF] dark:bg-[#020817] border-2 border-[#E2E8F0] dark:border-[#1E293B]">
                <CardHeader>
                  <CardTitle className="text-lg sm:text-xl text-gray-900 dark:text-white">
                    Register Academic Structure
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Basic Structure Configuration */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Basic Configuration
                      </h3>
                      
                      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 lg:gap-6">
                        <div className="space-y-2 flex flex-col">
                          <Label htmlFor="elevationLevel" className="text-sm font-medium text-gray-900 dark:text-white">
                            Elevation Level (Number of Stages)
                          </Label>
                          <Input
                            id="elevationLevel"
                            type="number"
                            min="1"
                            max="12"
                            value={formData.elevationLevel}
                            onChange={(e) => handleInputChange('elevationLevel', e.target.value)}
                            placeholder="e.g., 4"
                            className="h-10 bg-white dark:bg-[#020817] text-gray-900 dark:text-white border-2 border-[#E2E8F0] dark:border-[#1E293B] hover:border-[#CBD5E1] dark:hover:border-[#334155] focus:border-[#2563EB] dark:focus:border-[#3B82F6]"
                            required
                          />
                          <p className="text-xs text-gray-600 dark:text-gray-400">
                            Total stages before graduation
                          </p>
                        </div>

                        <div className="space-y-2 flex flex-col">
                          <Label htmlFor="className" className="text-sm font-medium text-gray-900 dark:text-white">
                            Class Name Convention
                          </Label>
                          <Input
                            id="className"
                            value={formData.className}
                            onChange={(e) => handleInputChange('className', e.target.value)}
                            placeholder="e.g., Form, Grade, Class"
                            className="h-10 bg-white dark:bg-[#020817] text-gray-900 dark:text-white border-2 border-[#E2E8F0] dark:border-[#1E293B] hover:border-[#CBD5E1] dark:hover:border-[#334155] focus:border-[#2563EB] dark:focus:border-[#3B82F6]"
                            required
                          />
                          <p className="text-xs text-gray-600 dark:text-gray-400">
                            Base naming for levels
                          </p>
                        </div>

                        <div className="space-y-2 flex flex-col">
                          <Label htmlFor="startingLevel" className="text-sm font-medium text-gray-900 dark:text-white">
                            Starting Level Number
                          </Label>
                          <Input
                            id="startingLevel"
                            type="number"
                            min="1"
                            max="20"
                            value={formData.startingLevel}
                            onChange={(e) => handleInputChange('startingLevel', e.target.value)}
                            placeholder="e.g., 1"
                            className="h-10 bg-white dark:bg-[#020817] text-gray-900 dark:text-white border-2 border-[#E2E8F0] dark:border-[#1E293B] hover:border-[#CBD5E1] dark:hover:border-[#334155] focus:border-[#2563EB] dark:focus:border-[#3B82F6]"
                            required
                          />
                          <p className="text-xs text-gray-600 dark:text-gray-400">
                            Level numbering starts from
                          </p>
                        </div>

                        <div className="space-y-2 flex flex-col">
                          <Label htmlFor="stage" className="text-sm font-medium text-gray-900 dark:text-white">
                            Stage Type
                          </Label>
                          <Select
                            value={formData.stage}
                            onValueChange={(value) => handleInputChange('stage', value)}
                          >
                            <SelectTrigger
                              id="stage"
                              className="h-10 bg-white dark:bg-[#020817] text-gray-900 dark:text-white border-2 border-[#E2E8F0] dark:border-[#1E293B] hover:border-[#CBD5E1] dark:hover:border-[#334155] focus:border-[#2563EB] dark:focus:border-[#3B82F6]"
                            >
                              <SelectValue placeholder="Select stage" />
                            </SelectTrigger>
                            <SelectContent className="bg-[#FFFFFF] dark:bg-[#020817] border-2 border-[#E2E8F0] dark:border-[#1E293B]">
                              <SelectItem value="progressive" className="text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-[#131E31]">
                                Progressive
                              </SelectItem>
                              <SelectItem value="final" className="text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-[#131E31]">
                                Final Stage
                              </SelectItem>
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-gray-600 dark:text-gray-400">
                            Progressive or terminal stage
                          </p>
                        </div>

                        <div className="space-y-2 flex flex-col">
                          <Label htmlFor="streamsPerLevel" className="text-sm font-medium text-gray-900 dark:text-white">
                            Streams per Level
                          </Label>
                          <Input
                            id="streamsPerLevel"
                            type="number"
                            min="1"
                            max="10"
                            value={formData.streamsPerLevel}
                            onChange={(e) => handleInputChange('streamsPerLevel', e.target.value)}
                            placeholder="e.g., 2"
                            className="h-10 bg-white dark:bg-[#020817] text-gray-900 dark:text-white border-2 border-[#E2E8F0] dark:border-[#1E293B] hover:border-[#CBD5E1] dark:hover:border-[#334155] focus:border-[#2563EB] dark:focus:border-[#3B82F6]"
                            required
                          />
                          <p className="text-xs text-gray-600 dark:text-gray-400">
                            Parallel groups per level
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Stream Names Configuration */}
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          Stream Names
                        </h3>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={addStreamName}
                          className="flex m-1 items-center gap-2 bg-white dark:bg-[#020817] text-gray-900 dark:text-white border-2 border-[#E2E8F0] dark:border-[#1E293B] hover:bg-gray-50 dark:hover:bg-[#131E31]"
                        >
                          <Plus className="h-4 w-4" />
                          Add Stream
                        </Button>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {formData.streamNames.map((name, index) => (
                          <div key={index} className="flex items-center gap-2">
                            <Input
                              value={name}
                              onChange={(e) => handleStreamNameChange(index, e.target.value)}
                              placeholder={`Stream ${index + 1} name (e.g., Red, Blue, Alpha)`}
                              className="bg-white dark:bg-[#020817] text-gray-900 dark:text-white border-2 border-[#E2E8F0] dark:border-[#1E293B] hover:border-[#CBD5E1] dark:hover:border-[#334155] focus:border-[#2563EB] dark:focus:border-[#3B82F6]"
                              required
                            />
                            {formData.streamNames.length > 1 && (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => removeStreamName(index)}
                                className="shrink-0 bg-white dark:bg-[#020817] text-gray-900 dark:text-white border-2 border-[#E2E8F0] dark:border-[#1E293B] hover:bg-gray-50 dark:hover:bg-[#131E31]"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>

                      {/* Stream Names Preview */}
                      {formData.streamNames.some(name => name && name.trim() !== '') && (
                        <div className="space-y-2">
                          <Label className="text-sm font-medium text-gray-900 dark:text-white">
                            Stream Preview:
                          </Label>
                          <div className="flex flex-wrap gap-2">
                            {formData.streamNames
                              .filter(name => name && name.trim() !== '')
                              .map((name, index) => (
                                <Badge 
                                  key={index} 
                                  variant="secondary" 
                                  className="m-2 px-3 py-1 bg-gray-100 text-gray-800 dark:bg-[#1E293B] dark:text-gray-200 border-2 border-[#E2E8F0] dark:border-[#1E293B]"
                                >
                                  {name}
                                </Badge>
                              ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Generated Levels Preview */}
                    {previewLevels.length > 0 && (
                      <div className="space-y-4 mt-2 p-4 border-2 rounded-lg bg-white/10 dark:bg-[#020817]/10 border-[#E2E8F0] dark:border-[#1E293B]">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          Generated Academic Levels
                        </h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                          {previewLevels.map((level, index) => (
                            <div 
                              key={index} 
                              className="p-3 rounded-lg bg-white dark:bg-[#020817] border-2 border-[#E2E8F0] dark:border-[#1E293B]"
                            >
                              <div className="font-medium text-center text-gray-900 dark:text-white">
                                {level}
                              </div>
                              <div className="text-xs text-gray-600 dark:text-gray-400 text-center mt-1">
                                {formData.streamNames.filter(name => name && name.trim() !== '').length} streams
                              </div>
                            </div>
                          ))}
                        </div>
                        
                        {/* Example Stream Combinations */}
                        {formData.streamNames.some(name => name && name.trim() !== '') && (
                          <div className="mt-4">
                            <Label className="text-sm font-medium text-gray-900 dark:text-white">
                              Example combinations:
                            </Label>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                              {previewLevels.slice(0, 2).map((level, levelIndex) => (
                                <div key={levelIndex} className="flex flex-wrap gap-1">
                                  {formData.streamNames
                                    .filter(name => name && name.trim() !== '')
                                    .map((streamName, streamIndex) => (
                                      <Badge 
                                        key={streamIndex} 
                                        variant="outline" 
                                        className="m-1 text-xs text-gray-900 dark:text-white border-2 border-[#E2E8F0] dark:border-[#1E293B] bg-white dark:bg-[#020817]"
                                      >
                                        {level} {streamName}
                                      </Badge>
                                    ))}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-4 pt-4 border-t border-[#E2E8F0] dark:border-[#1E293B]">
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={onBack} 
                        className="w-full sm:w-auto bg-white dark:bg-[#020817] text-gray-900 dark:text-white border-2 border-[#E2E8F0] dark:border-[#1E293B] hover:bg-gray-50 dark:hover:bg-[#131E31]"
                      >
                        Cancel
                      </Button>
                      <Button 
                        type="submit" 
                        disabled={isSaving}
                        className="flex ml-2 items-center gap-2 w-full sm:w-auto bg-[#2563EB] text-white hover:bg-[#1E40AF] dark:hover:bg-[#3B82F6] border-2 border-[#E2E8F0] dark:border-[#1E293B]"
                      >
                        <Save className="h-4 w-4" />
                        {isSaving ? "Registering..." : "Register Class"}
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </div>
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
};

export default ClassRegistrationForm;